import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { AlertTriangle, Clock, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { AdminDataTable } from "~/components/admin/data-table";
import { Avatar, AvatarFallback } from "~/components/ui/avatar";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { ConfirmDialog } from "~/components/ui/confirm-dialog";
import { Input } from "~/components/ui/input";
import { cn } from "~/lib/cn";
import { requirePermission } from "~/lib/functions/admin-access";
import {
  adminUserQueryKeys,
  adminUsersQueryOptions,
  cancelUserDeletionFn,
  deleteUserImmediatelyFn,
  scheduleUserDeletionFn,
  type AdminUserDto,
  type ImmediateDeletionDto,
} from "~/lib/functions/admin-users";
import {
  ADMIN_USER_SORTS,
  LAST_ADMIN_MESSAGE,
  type AdminUserSort,
} from "~/lib/shared/admin-users";
import { hasPermission } from "~/lib/shared/permissions";
import type { RoleKey } from "~/lib/shared/roles";
import { USER_DELETION_GRACE_PERIOD_DAYS } from "~/lib/shared/user-deletion";
import {
  deriveUserDeletionStatus,
  formatTimeRemaining,
} from "~/lib/shared/user-deletion-status";

import { DeleteUserDialog } from "./-components/delete-user-dialog";
import { ManageRolesDialog } from "./-components/manage-roles-dialog";
import { displayName, formatDate, initials } from "./-components/user-display";
import { RoleFilter, UserRolesCell } from "./-components/user-roles";
import {
  areRowActionsDisabled,
  UserRowActions,
} from "./-components/user-row-actions";
import { UsersEmptyState } from "./-components/users-empty-state";
import {
  listParamsFrom,
  nextSortSearch,
  PAGE_SIZE,
  usersSearchSchema,
  type UsersSearch,
} from "./-components/users-search";
import { WithdrawLeadsCard } from "./-components/withdraw-leads-card";

export const Route = createFileRoute("/admin/users/")({
  staticData: { title: "Users" },
  validateSearch: usersSearchSchema,
  loaderDeps: ({ search }) => listParamsFrom(search),
  beforeLoad: ({ context }) =>
    requirePermission(context.adminPermissions, "users:read"),
  loader: async ({ context, deps }) => {
    await context.queryClient.ensureQueryData(adminUsersQueryOptions(deps));
  },
  component: AdminUsersPage,
});

/** What the operator has to type to unlock the delete button. */
function confirmationPhrase(user: AdminUserDto): string {
  return user.email ?? user.id;
}

/** Shown beside the name only while a deletion is pending. */
function DeletionBadge({
  user,
}: {
  user: AdminUserDto;
}): React.ReactElement | null {
  const status = deriveUserDeletionStatus(
    user.scheduledForDeletionAt === null
      ? null
      : new Date(user.scheduledForDeletionAt),
  );

  switch (status.kind) {
    case "none":
      return null;
    case "overdue":
      return (
        <Badge
          variant="danger"
          size="sm"
          className="font-medium"
          title={`Was due ${formatDate(status.scheduledFor)}. The deletion workflow did not run.`}
        >
          <AlertTriangle className="size-3" />
          Deletion overdue
        </Badge>
      );
    case "scheduled":
      return (
        <Badge
          size="sm"
          className="border-amber-200 bg-amber-50 font-medium text-amber-800 hover:bg-amber-50"
          title={`${formatTimeRemaining(status.millisecondsRemaining)} left`}
        >
          <Clock className="size-3" />
          Deletion on{" "}
          {status.scheduledFor.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          })}
        </Badge>
      );
    default: {
      const exhaustive: never = status;
      throw new Error(`Unhandled deletion status: ${String(exhaustive)}`);
    }
  }
}

function UserNameCell({ user }: { user: AdminUserDto }): React.ReactElement {
  return (
    <div className="flex items-center gap-3">
      <Avatar className="size-8">
        <AvatarFallback className="bg-brand-50 text-xs font-medium text-brand-700">
          {initials(user)}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="font-medium text-foreground">
            {displayName(user)}
          </span>
          {user.coach && (
            <Link
              to="/$username"
              params={{ username: user.coach.username }}
              target="_blank"
              title="This user also owns a coach profile"
            >
              <Badge variant="cancelled" size="sm" className="font-medium">
                Coach
              </Badge>
            </Link>
          )}
          {user.isSelf && (
            <Badge variant="cancelled" size="sm" className="font-medium">
              You
            </Badge>
          )}
          <DeletionBadge user={user} />
        </div>
        <div className="text-xs text-muted-foreground">
          {user.email ?? "No email on record"}
        </div>
      </div>
    </div>
  );
}

function scheduledDeletionDate(): string {
  const date = new Date();
  date.setDate(date.getDate() + USER_DELETION_GRACE_PERIOD_DAYS);
  return formatDate(date);
}

/**
 * Clerk deleted, our row still here. This stays on screen until dismissed —
 * a toast would take the id the operator needs with it.
 */
interface HalfDeletedRecord {
  readonly userId: string;
  readonly clerkUserId: string | null;
  readonly email: string | null;
  readonly error: string;
}

function AdminUsersPage(): React.ReactElement {
  const navigate = useNavigate({ from: Route.fullPath });
  const params = listParamsFrom(Route.useSearch());
  const { search, role, sort, direction } = params;
  const currentPage = params.page;
  const queryClient = useQueryClient();

  /** Changes the URL search, keeping every param the caller does not name. */
  const updateSearch = (
    patch: Partial<UsersSearch>,
    options: { replace?: boolean } = {},
  ) => {
    navigate({
      search: (previous) => ({ ...previous, ...patch }),
      replace: options.replace,
    });
  };

  const [searchInput, setSearchInput] = useState(search);
  const { adminPermissions } = Route.useRouteContext();
  const canChangeRoles = hasPermission(adminPermissions, "roles:write");
  const [userManagingRoles, setUserManagingRoles] =
    useState<AdminUserDto | null>(null);
  const [userPendingSchedule, setUserPendingSchedule] =
    useState<AdminUserDto | null>(null);
  const [userPendingDeletion, setUserPendingDeletion] =
    useState<AdminUserDto | null>(null);
  const [halfDeleted, setHalfDeleted] = useState<HalfDeletedRecord | null>(
    null,
  );

  useEffect(() => {
    setSearchInput(search);
  }, [search]);

  useEffect(() => {
    if (searchInput === search) return;

    const timer = setTimeout(() => {
      navigate({
        search: (previous) => ({
          ...previous,
          q: searchInput === "" ? undefined : searchInput,
          page: undefined,
        }),
        replace: true,
      });
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput, search, navigate]);

  const { data, isFetching, isPlaceholderData } = useQuery({
    ...adminUsersQueryOptions(params),
    // Holding the previous page's rows through a refetch is what keeps the
    // empty state from flashing while the corrected page loads.
    placeholderData: keepPreviousData,
  });

  // The server clamps an out-of-range page and reports the page it actually
  // used — deleting the last row of the last page lands here. Reconciling only
  // on non-placeholder data means this never fires against a stale response
  // (which would fight a page the operator just clicked), and clamping is
  // idempotent, so the corrected URL settles in one navigation.
  useEffect(() => {
    if (!data || isPlaceholderData) return;
    if (data.page === currentPage) return;

    navigate({
      search: (previous) => ({ ...previous, page: data.page }),
      replace: true,
    });
  }, [data, isPlaceholderData, currentPage, navigate]);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: adminUserQueryKeys.all });

  const scheduleMutation = useMutation({
    mutationFn: scheduleUserDeletionFn,
    onSuccess: (result) => {
      invalidate();
      setUserPendingSchedule(null);
      // `unsupported` comes back alongside `success: true` when the Workflows
      // binding is missing: this attempt scheduled nothing, so it has to be
      // checked before `success` or the toast reports a deletion that does not
      // exist. It says nothing about a deletion recorded earlier, which
      // survives untouched — hence the split below.
      if (result.unsupported && result.existingDeletionPending) {
        toast.warning(
          result.existingDeletionAt
            ? `Nothing changed — the deletion workflow service is unavailable. This user is still scheduled for deletion on ${formatDate(result.existingDeletionAt)}.`
            : "Nothing changed — the deletion workflow service is unavailable. This user is still scheduled for deletion.",
        );
      } else if (result.unsupported) {
        toast.warning(
          "Nothing was scheduled — the deletion workflow service is unavailable.",
        );
      } else if (result.success) {
        toast.success(
          `Deletion scheduled in ${USER_DELETION_GRACE_PERIOD_DAYS} days.`,
        );
      } else {
        toast.error(result.error ?? "Could not schedule the deletion.");
      }
    },
    onError: () => toast.error("Could not schedule the deletion."),
  });

  const cancelMutation = useMutation({
    mutationFn: cancelUserDeletionFn,
    onSuccess: (result) => {
      invalidate();
      if (result.unsupported) {
        toast.warning(
          "Nothing was cancelled — the deletion workflow service is unavailable. The scheduled deletion may still be pending.",
        );
      } else if (result.success) {
        toast.success("Scheduled deletion cancelled.");
      } else {
        toast.error(result.error ?? "Could not cancel the deletion.");
      }
    },
    onError: () => toast.error("Could not cancel the deletion."),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteUserImmediatelyFn,
    onSuccess: (result: ImmediateDeletionDto) => {
      invalidate();

      switch (result.status) {
        case "deleted":
          setUserPendingDeletion(null);
          toast.success(
            result.clerkAccountAlreadyAbsent
              ? "User deleted. The Clerk account was already gone."
              : "User deleted from Clerk and from our database.",
          );
          return;
        case "user_not_found":
          setUserPendingDeletion(null);
          toast.error("That user no longer exists.");
          return;
        case "last_admin":
          setUserPendingDeletion(null);
          toast.error(LAST_ADMIN_MESSAGE);
          return;
        case "workflow_cancel_failed":
          toast.error(
            `Nothing was deleted: terminating the pending deletion workflow was attempted and failed (${result.error}).`,
          );
          return;
        case "workflow_service_unavailable":
          toast.warning(
            "Nothing was deleted: this environment cannot reach the workflow service, so the pending deletion workflow could not be terminated. Deleting now would leave it armed against a missing account — retry from an environment with Workflows available.",
          );
          return;
        case "clerk_delete_failed":
          toast.error(
            `Nothing was deleted: Clerk refused the delete (${result.error}).`,
          );
          return;
        case "database_delete_failed":
          setUserPendingDeletion(null);
          setHalfDeleted({
            userId: result.userId,
            clerkUserId: result.clerkUserId,
            email: userPendingDeletion?.email ?? null,
            error: result.error,
          });
          return;
        default: {
          const exhaustive: never = result;
          throw new Error(`Unhandled deletion result: ${String(exhaustive)}`);
        }
      }
    },
    onError: () =>
      toast.error(
        "The delete request failed before it reached Clerk. Nothing was deleted.",
      ),
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // While `keepPreviousData` holds the old rows on screen, the row under the
  // cursor may belong to a result set the URL no longer points at, so nothing
  // that acts on a specific row may be live.
  const rowActionsDisabled = areRowActionsDisabled({
    isMutating:
      scheduleMutation.isPending ||
      cancelMutation.isPending ||
      deleteMutation.isPending,
    isShowingPreviousRows: isPlaceholderData,
  });

  const changeSort = (column: AdminUserSort) =>
    updateSearch(nextSortSearch({ sort, direction }, column));

  const columns: ColumnDef<AdminUserDto>[] = [
    {
      id: "name",
      header: "Name",
      cell: ({ row }) => <UserNameCell user={row.original} />,
    },
    {
      id: "roles",
      header: "Roles",
      cell: ({ row }) => <UserRolesCell roles={row.original.roles} />,
    },
    {
      id: "joined",
      header: "Joined",
      cell: ({ row }) => (
        <span className="text-sm whitespace-nowrap text-muted-foreground">
          {formatDate(row.original.createdAt)}
        </span>
      ),
    },
    {
      id: "actions",
      header: () => <span className="sr-only">Actions</span>,
      meta: { align: "end", className: "w-12" },
      cell: ({ row }) => (
        <UserRowActions
          user={row.original}
          disabled={rowActionsDisabled}
          onManageRoles={canChangeRoles ? setUserManagingRoles : undefined}
          onScheduleRequest={setUserPendingSchedule}
          onCancel={(target) =>
            cancelMutation.mutate({ data: { userId: target.id } })
          }
          onDeleteRequest={setUserPendingDeletion}
        />
      ),
    },
  ];

  const changeRole = (nextRole: RoleKey | undefined) =>
    updateSearch({ role: nextRole, page: undefined });

  const goToPage = (nextPage: number) => updateSearch({ page: nextPage });

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-medium text-foreground">Users</h1>
        <p className="text-sm text-muted-foreground">
          Find an account, see who has which access, or schedule its deletion.
        </p>
      </div>

      {halfDeleted && (
        <div className="rounded-lg border border-red-300 bg-red-50 p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
            <div className="text-sm text-red-800">
              <p className="font-medium">
                Half-deleted account — finish this by hand
              </p>
              <p className="mt-1">
                The Clerk account for{" "}
                <span className="font-medium">
                  {halfDeleted.email ?? "this user"}
                </span>{" "}
                was deleted, but our database row was not. Delete it directly:
              </p>
              <ul className="mt-2 space-y-0.5 font-mono text-xs">
                <li>user id: {halfDeleted.userId}</li>
                <li>clerk id: {halfDeleted.clerkUserId ?? "—"}</li>
              </ul>
              <p className="mt-2 text-xs">
                Database error: {halfDeleted.error}
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="ml-auto"
              onClick={() => setHalfDeleted(null)}
            >
              Dismiss
            </Button>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-sm flex-1">
          <Search className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={searchInput}
            onValueChange={setSearchInput}
            placeholder="Search by name or email..."
            className="mb-0 pl-9"
          />
          {isFetching && (
            <span className="absolute top-1/2 right-3 -translate-y-1/2 text-xs text-muted-foreground">
              Updating…
            </span>
          )}
        </div>
        <RoleFilter value={role} onChange={changeRole} />
        <span
          className={cn(
            "text-sm text-muted-foreground",
            isFetching && "opacity-50",
          )}
        >
          {total} user{total === 1 ? "" : "s"}
        </span>
      </div>

      <AdminDataTable
        data={rows}
        columns={columns}
        getRowId={(user) => user.id}
        sorts={ADMIN_USER_SORTS}
        sort={sort}
        direction={direction}
        onSortChange={changeSort}
        // Dimming makes the disabled row actions read as a deliberate pause
        // rather than an unresponsive screen.
        isStale={rowActionsDisabled}
        emptyState={
          <UsersEmptyState
            search={search}
            role={role}
            onClearRole={() => changeRole(undefined)}
          />
        }
      />

      {pageCount > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            Page {currentPage} of {pageCount}
          </span>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              disabled={currentPage <= 1}
              onClick={() => goToPage(currentPage - 1)}
            >
              Previous
            </Button>
            <Button
              size="sm"
              disabled={currentPage >= pageCount}
              onClick={() => goToPage(currentPage + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      <WithdrawLeadsCard />

      {userManagingRoles && (
        <ManageRolesDialog
          key={userManagingRoles.id}
          user={userManagingRoles}
          onClose={() => setUserManagingRoles(null)}
        />
      )}

      {userPendingSchedule && (
        <ConfirmDialog
          open
          onOpenChange={(open) => {
            if (!open && !scheduleMutation.isPending) {
              setUserPendingSchedule(null);
            }
          }}
          title={`Schedule ${displayName(userPendingSchedule)} for deletion?`}
          description={`The account for ${confirmationPhrase(userPendingSchedule)} and its data are deleted on ${scheduledDeletionDate()}, ${USER_DELETION_GRACE_PERIOD_DAYS} days from now. Until then you can cancel it from this row's menu.`}
          confirmLabel="Schedule deletion"
          loadingLabel="Scheduling…"
          isLoading={scheduleMutation.isPending}
          onConfirm={() =>
            scheduleMutation.mutate({
              data: { userId: userPendingSchedule.id },
            })
          }
        />
      )}

      {userPendingDeletion && (
        <DeleteUserDialog
          key={userPendingDeletion.id}
          user={userPendingDeletion}
          confirmationPhrase={confirmationPhrase(userPendingDeletion)}
          isDeleting={deleteMutation.isPending}
          onCancel={() => setUserPendingDeletion(null)}
          onConfirm={() =>
            deleteMutation.mutate({
              data: { userId: userPendingDeletion.id },
            })
          }
        />
      )}
    </div>
  );
}
